'use client';

import { useEffect, useState } from 'react';

// Popup anketa za prve posetioce: XAUUSD Elliott Wave A/B scenario.
// Prikazuje se jednom (localStorage), ne prikazuje se onima koji su već
// glasali (server pamti glas po httpOnly cookie-ju), niti na /xauusd gde
// već postoji puna anketa, niti na /live (owner, 2026-09-08 — konkuriše
// gate-u Zajednice, koji ima svoj jedan jasan CTA). Glasovi idu u isti
// zbir kao anketa na /xauusd i popup na fxdoctor.pro.
const POLL_ID = 'xauusd-2026';
const DONE_KEY = 'smt_poll_xauusd-2026_done';
// 2026-10-06: popup više ne iskače odmah (pokrivao je sadržaj prvim posetiocima,
// npr. polaznicima Uvoda i SEO posetiocima na /analiza-zlata-danas). Prikazuje se
// tek kad posetilac pokaže interesovanje: ENGAGED_DELAY_MS posle prvog skrola,
// ili najkasnije FALLBACK_DELAY_MS ako ne skroluje. Nikad u toku registracije/prijave.
const ENGAGED_DELAY_MS = 15000;
const FALLBACK_DELAY_MS = 45000;
const SCROLL_THRESHOLD_PX = 300;
const EXCLUDED_PREFIXES = [
  '/xauusd', '/live', '/sign-up-login-screen', '/auth', '/verify-account',
  '/admin', '/settings', '/messages', '/inbox', '/chat',
];

type Choice = 'a' | 'b' | null;

const OPTIONS = {
  a: { color: '#0E8455', title: 'Rast ka 6.000+' },
  b: { color: '#C8362A', title: 'Pad ka 3.400 i niže' },
} as const;

// Pamti "viđeno" u pregledaču (localStorage) I po nalogu (PollSeen, za ulogovane), da se
// popup ne vraća na drugom računaru/telefonu istog korisnika.
function markDone(action: 'vote' | 'dismiss' = 'dismiss') {
  try { localStorage.setItem(DONE_KEY, '1'); } catch { /* private mode */ }
  try {
    fetch(`/api/poll/${POLL_ID}/seen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
      keepalive: true,
    }).catch(() => { /* best-effort */ });
  } catch { /* best-effort */ }
}

export default function PollPopup() {
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ a: 0, b: 0 });
  const [choice, setChoice] = useState<Choice>(null);
  const [hover, setHover] = useState<Choice>(null);

  useEffect(() => {
    try { if (localStorage.getItem(DONE_KEY)) return; } catch { return; }
    let cancelled = false;
    let shown = false;
    let engagedTimer: ReturnType<typeof setTimeout> | null = null;

    const show = async () => {
      if (shown || cancelled) return;
      shown = true;
      // window.location umesto usePathname: proveri rutu u trenutku prikaza
      const path = window.location.pathname;
      if (EXCLUDED_PREFIXES.some((p) => path.startsWith(p))) return;
      try {
        const res = await fetch(`/api/poll/${POLL_ID}`, { cache: 'no-store' });
        if (!res.ok || cancelled) return;
        const d = await res.json();
        // Već glasano (cookie) ili već viđeno/glasano po nalogu na bilo kom uređaju.
        if (d.choice || d.seen) {
          try { localStorage.setItem(DONE_KEY, '1'); } catch { /* private mode */ }
          return;
        }
        if (cancelled) return;
        setCounts({ a: d.a ?? 0, b: d.b ?? 0 });
        setOpen(true);
      } catch { /* bez mreže nema popup-a */ }
    };

    const onScroll = () => {
      if (engagedTimer || window.scrollY < SCROLL_THRESHOLD_PX) return;
      engagedTimer = setTimeout(show, ENGAGED_DELAY_MS);
      window.removeEventListener('scroll', onScroll);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const fallbackTimer = setTimeout(show, FALLBACK_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      if (engagedTimer) clearTimeout(engagedTimer);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  function close() {
    markDone();
    setOpen(false);
  }

  async function vote(next: 'a' | 'b') {
    if (choice) return;
    markDone('vote');
    setChoice(next);
    setCounts((c) => ({ ...c, [next]: c[next] + 1 }));
    try {
      const res = await fetch(`/api/poll/${POLL_ID}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ choice: next, source: 'smt' }),
      });
      if (res.ok) {
        const d = await res.json();
        setCounts({ a: d.a ?? 0, b: d.b ?? 0 });
      }
    } catch { /* optimistički prikaz ostaje */ }
  }

  if (!open) return null;

  const tot = counts.a + counts.b;
  const pct = (v: number) => (tot ? Math.round((v / tot) * 100) : 0);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={close}
    >
      <div
        className="w-full max-w-md rounded-2xl animate-fade-in"
        style={{ background: '#FFFFFF', border: '1px solid #E4E8EE', padding: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.5, color: '#0E7C86' }}>
              ELLIOTT WAVE · XAUUSD
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0E2A3D', marginTop: 6, lineHeight: 1.25 }}>
              Kuda ide zlato — koji scenario vidite kao verovatniji?
            </div>
            <div style={{ fontSize: 13, color: '#7C8794', marginTop: 4 }}>
              Dugoročna Elliott Wave projekcija FX Doctora
            </div>
          </div>
          <button
            onClick={close}
            aria-label="Zatvori"
            style={{ color: '#98A1B0', fontSize: 20, lineHeight: 1, padding: 4, marginTop: -2 }}
          >
            ×
          </button>
        </div>

        <div className="flex flex-col gap-2.5" style={{ marginTop: 16 }}>
          {(['a', 'b'] as const).map((key) => {
            const o = OPTIONS[key];
            const active = choice === key || (!choice && hover === key);
            return (
              <button
                key={key}
                onClick={() => vote(key)}
                onMouseEnter={() => setHover(key)}
                onMouseLeave={() => setHover(null)}
                disabled={Boolean(choice)}
                className="text-left w-full"
                style={{
                  borderRadius: 12, padding: '14px 16px', background: '#FFFFFF',
                  border: `2px solid ${active ? o.color : '#E4E8EE'}`,
                  cursor: choice ? 'default' : 'pointer',
                }}
              >
                <div className="flex items-center gap-2.5">
                  <span
                    style={{
                      width: 26, height: 26, borderRadius: 8, background: o.color, color: '#fff',
                      fontSize: 13, fontWeight: 800, display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    {key.toUpperCase()}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#0E2A3D' }}>{o.title}</span>
                  {choice && (
                    <span className="ml-auto" style={{ fontSize: 15, fontWeight: 700, color: o.color, fontVariantNumeric: 'tabular-nums' }}>
                      {pct(counts[key])}%
                    </span>
                  )}
                </div>
                {choice && (
                  <div style={{ height: 8, borderRadius: 4, background: '#E9EDF2', marginTop: 10, overflow: 'hidden' }}>
                    <div style={{ height: 8, borderRadius: 4, background: o.color, transition: 'width .35s ease', width: `${pct(counts[key])}%` }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between" style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: '#7C8794' }}>
            {choice ? 'Hvala na glasu!' : 'Anonimno · jedan klik'}
          </div>
          <a
            href="/xauusd"
            onClick={() => markDone()}
            style={{ fontSize: 13, fontWeight: 700, color: '#0E7C86' }}
          >
            Pogledaj celu analizu →
          </a>
        </div>
      </div>
    </div>
  );
}
